import React, { useState, useEffect, useRef } from "react";
import useRouting from "../hooks/useRouting";
import usePageTracking from "../hooks/usePageTracking";
import { trackEvent } from "../utils/trackEvent";
import environment from "../environment";
import axios from "axios";
import { getHeaders, getStorage, handleApiError } from "../utils/apiUtils";
import "./PersonalDetails.css";
import personal_details from "../assets/images/personal_details.svg";
import GreenTick from "../assets/images/GreenTick.svg";
import pencil_4887028 from "../assets/images/priceEdit.svg";
import Header from "./Header";
//import { usee } from "react";

const PersonalDetails = () => {
  const hasFetchedRef = useRef(false);
  const latestPanRequestRef = useRef(null);
  const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  const [panNumber, setPanNumber] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [dateOfBirthDisplay, setDateOfBirthDisplay] = useState("");
  const [gender, setGender] = useState("");
  const [email, setEmail] = useState("");
  const [aadharNumber, setAadharNumber] = useState("");
  const [consent1, setConsent1] = useState(false);
  const [isPanValid, setIsPanValid] = useState(false);
  const [isPanLoading, setIsPanLoading] = useState(false);
  const [panError, setPanError] = useState("");
  const [whatsappConsent, setWhatsappConsent] = useState(true);
  const [isPanEditable, setIsPanEditable] = useState(true);
  const { navigateWithContext, navigateBack } = useRouting();

  // Function to capitalize first letter and make rest lowercase
  const capitalizeFirstLetter = (str) => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };
  
  // Track page visit
  usePageTracking("personal-details");
  useEffect(() => { trackEvent("loan_personal_details_viewed"); }, []);

  // Helper function to convert date format from dd/MM/yyyy or dd-MM-yyyy to yyyy-MM-dd (for HTML date input)
  const convertDateFormat = (dateString) => {
    if (!dateString) return "";

    try {
      // Handle different possible formats
      let parts;

      if (dateString.includes("-")) {
        parts = dateString.split("-");
      } else if (dateString.includes("/")) {
        parts = dateString.split("/");
      } else {
        return dateString; // Return as is if format is unknown
      }

      // Assuming input format is dd-MM-yyyy or dd/MM/yyyy
      if (parts.length === 3) {
        const day = parts[0].padStart(2, "0");
        const month = parts[1].padStart(2, "0");
        const year = parts[2];

        // Return in yyyy-MM-dd format for HTML date input
        return `${year}-${month}-${day}`;
      }

      return dateString; // Return original if parsing fails
    } catch (error) {
      console.error("Error converting date format:", error);
      return dateString;
    }
  };

  // Helper function to convert date to dd-MMM-yyyy format (e.g., 01-Nov-2002)
  const formatDateToDisplay = (dateString) => {
    if (!dateString) return "";

    try {
      let parts;
      let day, month, year;

      // Handle different input formats
      if (dateString.includes("-")) {
        parts = dateString.split("-");
        // Check if it's already in yyyy-MM-dd format (from date input)
        if (parts.length === 3 && parts[0].length === 4) {
          // yyyy-MM-dd format
          year = parts[0];
          month = parts[1];
          day = parts[2];
        } else if (parts.length === 3 && parts[2].length === 4) {
          // dd-MM-yyyy format
          day = parts[0].padStart(2, "0");
          month = parts[1].padStart(2, "0");
          year = parts[2];
        } else {
          // Already in dd-MMM-yyyy format
          return dateString;
        }
      } else if (dateString.includes("/")) {
        parts = dateString.split("/");
        if (parts.length === 3) {
          day = parts[0].padStart(2, "0");
          month = parts[1].padStart(2, "0");
          year = parts[2];
        } else {
          return dateString;
        }
      } else {
        return dateString;
      }

      if (parts.length === 3) {
        const monthNames = [
          "Jan", "Feb", "Mar", "Apr", "May", "Jun",
          "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        ];
        
        const monthIndex = parseInt(month, 10) - 1;
        if (monthIndex >= 0 && monthIndex < 12) {
          return `${day}-${monthNames[monthIndex]}-${year}`;
        }
      }

      return dateString;
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString;
    }
  };

  // Function to fetch PAN details from third-party API
  const fetchPanDetailsFromAPI = async (panNumber, onlyFillMissing = false, existingFields = {}) => {
    // Assign a unique token for this specific request to detect stale responses
    const requestToken = Symbol();
    latestPanRequestRef.current = requestToken;

    try {
      setIsPanLoading(true);
      setIsPanValid(false);
      setPanError("");
      const applicationId = getStorage("application_id");
      if (!applicationId) {
        console.error("Application ID not found in localStorage");
        setPanError("Application ID not found. Please refresh the page.");
        return;
      }

      const payload = {
        value: panNumber,
      };

      const response = await axios.post(
        `${environment.apiurl}ayushpay/applications/${applicationId}/pan_plus_profile/fetch-pan-detail/`,
        payload,
        { headers: getHeaders() }
      );

      // Discard stale response if a newer PAN request was started
      if (latestPanRequestRef.current !== requestToken) return;

      // Handle successful response
      if (response?.data) {
        console.log("PAN details fetched successfully:", response.data);
        setIsPanValid(true);
        setPanError("");

        // Auto-fill form fields if data is available
        // Only fill if field is empty or if onlyFillMissing is false
        if (response.data?.msg?.first_name && (!onlyFillMissing || !existingFields.firstName)) {
          setFirstName(capitalizeFirstLetter(response.data?.msg?.first_name));
        }
        if (response.data?.msg?.last_name && (!onlyFillMissing || !existingFields.lastName)) {
          setLastName(capitalizeFirstLetter(response.data?.msg?.last_name));
        }
        if (response.data?.msg?.date_of_birth && (!onlyFillMissing || !existingFields.dateOfBirth)) {
          // Convert date format from dd/MM/yyyy to yyyy-MM-dd (for storage)
          const dateString = response.data?.msg?.date_of_birth;
          const convertedDate = convertDateFormat(dateString);
          setDateOfBirth(convertedDate);
          // Also set the display format (01-Nov-2002)
          const displayDate = formatDateToDisplay(dateString);
          setDateOfBirthDisplay(displayDate);
        }
        if (response.data?.msg?.gender && (!onlyFillMissing || !existingFields.gender)) {
          setGender(response.data?.msg?.gender);
        }
        if (response.data?.msg?.email && (!onlyFillMissing || !existingFields.email)) {
          setEmail(response.data?.msg?.email);
        }
        if (response.data?.msg?.aadhar_number && (!onlyFillMissing || !existingFields.aadharNumber)) {
          setAadharNumber(response.data?.msg?.aadhar_number);
        }
      }
    } catch (error) {
      // Discard stale error if a newer PAN request was started
      if (latestPanRequestRef.current !== requestToken) return;

      const errorMessage = handleApiError(error);
      console.error("Error fetching PAN details:", errorMessage);
      setIsPanValid(false);

      // Set user-friendly error message
      let displayError = "Invalid PAN number. Please check and try again.";
      if (error.response?.data?.message) {
        displayError = error.response.data.message;
      } else if (error.response?.data?.msg) {
        displayError = typeof error.response.data.msg === 'string'
          ? error.response.data.msg
          : "Invalid PAN number. Please check and try again.";
      } else if (error.message) {
        displayError = error.message;
      }
      setPanError(displayError);
    } finally {
      if (latestPanRequestRef.current === requestToken) {
        setIsPanLoading(false);
      }
    }
  };

  // Fetch and prefill personal details on page load
  useEffect(() => {
    // Prevent duplicate API calls in StrictMode
    if (hasFetchedRef.current) {
      return;
    }
    hasFetchedRef.current = true;
    
    const fetchPersonalDetails = async () => {
      try {
        const applicationId = getStorage("application_id");
        if (!applicationId) {
          console.error("Application ID not found in localStorage");
          return;
        }

        // First priority: Fetch from DB (personal model)
        const response = await axios.get(
          `${environment.apiurlv3_webapi}applications/${applicationId}/view/data/?allowed=personal`,
          { headers: getHeaders() }
        );

        if (response?.data?.msg) {
          const personalData = response.data.msg;
          console.log("Personal details fetched successfully:", personalData);

          // Find personal model data
          const personalModel = personalData["personal"];

          // Prefill PAN number from DB first (highest priority)
          if (personalModel?.pan_number) {
            setPanNumber(personalModel.pan_number);
            setPanError("");

            // Reject invalid-format PAN from DB immediately — make editable so user can fix it
            if (!PAN_REGEX.test(personalModel.pan_number)) {
              setIsPanValid(false);
              setIsPanEditable(true);
              setPanError("Invalid PAN format. Please re-enter your PAN.");
              return;
            }

            setIsPanValid(true);
            setIsPanEditable(false); // Make PAN non-editable when prefilled from DB
            
            // Track which fields were set from DB
            const dbFields = {
              firstName: personalModel?.first_name ? capitalizeFirstLetter(personalModel.first_name) : null,
              lastName: personalModel?.last_name ? capitalizeFirstLetter(personalModel.last_name) : null,
              email: personalModel?.email || null,
              dateOfBirth: personalModel?.date_of_birth || null,
              gender: personalModel?.gender || null,
              aadharNumber: personalModel?.aadhar_number ? personalModel.aadhar_number.toString().replace(/\D/g, "") : null
            };
            
            // Prefill other fields from DB if available
            if (dbFields.firstName) {
              setFirstName(dbFields.firstName);
            }
            if (dbFields.lastName) {
              setLastName(dbFields.lastName);
            }
            if (dbFields.email) {
              setEmail(dbFields.email);
            }
            if (dbFields.dateOfBirth) {
              setDateOfBirth(dbFields.dateOfBirth);
              // Format for display if it's not already in yyyy-MM-dd format
              const displayDate = formatDateToDisplay(dbFields.dateOfBirth);
              setDateOfBirthDisplay(displayDate || dbFields.dateOfBirth);
            }
            if (dbFields.gender) {
              setGender(dbFields.gender);
            }
            if (dbFields.aadharNumber && dbFields.aadharNumber.length <= 12) {
              setAadharNumber(dbFields.aadharNumber);
            }
            
            // If DB has PAN but missing other fields, fetch from third-party API
            const hasAllFields = dbFields.firstName && 
                                 dbFields.lastName && 
                                 dbFields.dateOfBirth && 
                                 dbFields.gender;
            
            if (!hasAllFields && personalModel.pan_number) {
              // Fetch missing details from third-party API
              // Pass current DB values to avoid overwriting them
              await fetchPanDetailsFromAPI(personalModel.pan_number, true, dbFields);
            }
          } else {
            // If DB doesn't have PAN, keep it editable
            setIsPanEditable(true);
          }
        }
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error("Error fetching personal details:", errorMessage);
        // Don't block the user if API fails, just log the error
      }
    };

    fetchPersonalDetails();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePanChange = async (e) => {
    const value = e.target.value.toUpperCase();
    // PAN format: AAAAA9999A
    if (value.length <= 10) {
      setPanNumber(value);
      // Clear error when user starts typing
      if (panError) {
        setPanError("");
      }

      // Call API when PAN is complete (10 characters)
      if (value.length === 10) {
        if (!PAN_REGEX.test(value)) {
          setPanError("Invalid PAN format. Expected format: ABCDE1234F");
          setIsPanValid(false);
          return;
        }
        await fetchPanDetails(value);
      } else {
        // Reset validation state if PAN is incomplete
        setIsPanValid(false);
      }
    }
  };

  // Handle edit PAN button click - reset to initial state
  const handleEditPan = () => {
    setPanNumber("");
    setIsPanValid(false);
    setPanError("");
    setIsPanEditable(true);
    setFirstName("");
    setLastName("");
    setDateOfBirth("");
    setDateOfBirthDisplay("");
    setGender("");
    setEmail("");
    setAadharNumber("");
  };

  // Handle date input change - convert from display format to yyyy-MM-dd
  const handleDateChange = (e) => {
    const inputValue = e.target.value;
    setDateOfBirthDisplay(inputValue);
    
    // Try to parse the input format (01-Nov-2002 or 01/01/2002)
    if (inputValue) {
      const parsedDate = parseDateFromDisplay(inputValue);
      if (parsedDate) {
        setDateOfBirth(parsedDate);
      }
    } else {
      setDateOfBirth("");
    }
  };

  // Parse date from display format (01-Nov-2002 or 01/01/2002) to yyyy-MM-dd
  const parseDateFromDisplay = (dateString) => {
    if (!dateString) return "";

    try {
      let parts;
      let day, month, year;

      // Check if it's in dd-MMM-yyyy format (e.g., 01-Nov-2002)
      if (dateString.includes("-") && dateString.length > 10) {
        parts = dateString.split("-");
        if (parts.length === 3) {
          day = parts[0].padStart(2, "0");
          const monthNames = [
            "Jan", "Feb", "Mar", "Apr", "May", "Jun",
            "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
          ];
          const monthIndex = monthNames.findIndex(m => m.toLowerCase() === parts[1].toLowerCase());
          if (monthIndex >= 0) {
            month = String(monthIndex + 1).padStart(2, "0");
            year = parts[2];
            return `${year}-${month}-${day}`;
          }
        }
      } else if (dateString.includes("/")) {
        // Handle dd/MM/yyyy format
        parts = dateString.split("/");
        if (parts.length === 3) {
          day = parts[0].padStart(2, "0");
          month = parts[1].padStart(2, "0");
          year = parts[2];
          return `${year}-${month}-${day}`;
        }
      } else if (dateString.includes("-") && dateString.length === 10) {
        // Already in yyyy-MM-dd format
        return dateString;
      }

      return "";
    } catch (error) {
      console.error("Error parsing date:", error);
      return "";
    }
  };

  // Function to fetch PAN details (called when user enters PAN)
  const fetchPanDetails = async (panNumber) => {
    await fetchPanDetailsFromAPI(panNumber, false);
  };

  const handleAadharChange = (e) => {
    const value = e.target.value.replace(/\D/g, ""); // Only allow digits
    if (value.length <= 12) {
      setAadharNumber(value);
    }
  };


  
  const handleSubmit = async () => {
    trackEvent("loan_personal_details_continue_clicked", {
      credit_report_consent: consent1,
      whatsapp_consent: whatsappConsent,
    });
    const personalData = {
      panNumber,
      firstName,
      lastName,
      dateOfBirth,
      gender,
      email,
      aadharNumber,
      consents: {
        creditReport: consent1,
        whatsapp: whatsappConsent,
      },
    };

    const isValid =
      isPanValid &&
      panNumber &&
      firstName &&
      lastName &&
      dateOfBirth &&
      gender &&
      aadharNumber &&
      consent1 &&
      whatsappConsent;

    if (isValid) {
      try {
        // Prepare payload for API
        const payload = {
          data: [
            {
              model_name: "personal",
              field: [
                {
                  key: "first_name",
                  value: firstName,
                },
                {
                  key: "middle_name",
                  value: "",
                },
                {
                  key: "last_name",
                  value: lastName,
                },
                {
                  key: "email",
                  value: email,
                },
                {
                  key: "date_of_birth",
                  value: dateOfBirth,
                },
                {
                  key: "gender",
                  value: gender,
                },
                {
                  key: "aadhar_number",
                  value: aadharNumber,
                },
                {
                  key: "pan_number",
                  value: panNumber,
                },
              ],
            },
          ],
        };

        const applicationId = getStorage("application_id");
        if (!applicationId) {
          console.error("Application ID not found in localStorage");
          return;
        }

        // Call API to save personal details
        const response = await axios.post(
          `${environment.apiurl}applications/${applicationId}/save/data/`,
          payload,
          { headers: getHeaders() }
        );

        console.log("Personal details saved successfully:", response.data);

        // Save to localStorage for local reference
        localStorage.setItem("personalDetails", JSON.stringify(personalData));

        // Navigate to next step
        navigateWithContext("address-details");
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error("Error saving personal details:", errorMessage);
        // You might want to show an error message to the user here
        // For now, we'll still allow navigation for better UX
        localStorage.setItem("personalDetails", JSON.stringify(personalData));

        navigateWithContext("address-details");
      }
    }
  };

  const handleBack = () => {
    // Use new routing
    navigateBack("patient-info");
  };

  const isAadharValid = aadharNumber.length === 12;
  const isEmailValid = email.includes("@") && email.includes(".");
  let isFormValid = false;
  if(email) {
    isFormValid =
    isPanValid &&
    firstName &&
    lastName &&
    dateOfBirth &&
    gender &&
    isEmailValid &&
    isAadharValid &&
    consent1;
  } else {
    isFormValid =
    isPanValid &&
    firstName &&
    lastName &&
    dateOfBirth &&
    gender &&
    isAadharValid &&
    consent1;
  }


  const handleconsentChange = (e) => {
    const checked = e.target.checked;
    setConsent1(checked);
    localStorage.setItem("consent1", checked);
  }

  useEffect(() => {
  const storedConsent1 = localStorage.getItem("consent1");
  if (storedConsent1 !== null) {
    setConsent1(storedConsent1 === "true");
  }
}, []);


  return (
    <div className="loan-container">
      <Header showBackButton={true} onBackClick={handleBack} />
      <div className="main-content">
        <div className="hero-section">
          <div className="hero-icon">
            <img src={personal_details} alt="personal Details" />
          </div>
          <h1 className="hero-title">
            Please share your
            <br />
            personal details
          </h1>
        </div>

        <div className="form-section">
          <div className="personal-detail-container">
            <div className="personal-detail-form-group">
              <div className="form-group-container">
                <label className="form-label">Enter PAN Number <span className="text-red-500">*</span></label>
                <div className="pancard-input-container">
                  <input
                    type="text"
                    className="form-input"
                    placeholder="AAAXX0000X"
                    value={panNumber}
                    onChange={handlePanChange}
                    maxLength={10}
                    style={{ 
                      paddingRight: isPanLoading || isPanValid || !isPanEditable ? (isPanValid && !isPanEditable ? '60px' : '40px') : '12px' 
                    }}
                    disabled={isPanLoading || !isPanEditable}
                  />
                  {isPanLoading && (
                    <div className="pancard-loader">
                      <div className="loader"></div>
                    </div>
                  )}
                  {!isPanLoading && !isPanEditable && (
                    <img
                      src={pencil_4887028}
                      alt="Edit PAN"
                      onClick={handleEditPan}
                      style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: '20px',
                        height: '20px',
                        cursor: 'pointer'
                      }}
                    />
                  )}
                  {isPanValid && !isPanLoading && isPanEditable && (
                    <img
                      className="input-success-icon"
                      src={GreenTick}
                      alt="Valid PAN"
                      style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: '20px',
                        height: '20px'
                      }}
                    />
                  )}
                </div>
                {panError && (
                  <p style={{ 
                    color: '#ef4444', 
                    fontSize: '12px', 
                    marginTop: '4px',
                    marginLeft: '4px'
                  }}>
                    {panError}
                  </p>
                )}
              </div>
              {isPanValid && (
                <>
                  <div className="form-group-container">
                    <label className="form-label">First Name <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Ravi"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                  </div>

                  <div className="form-group-container">
                    <label className="form-label">Last Name <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Kumar"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                  </div>

                  <div className="form-group-container">
                    <label className="form-label">Date of Birth <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      className="form-input date-form-input"
                      placeholder="DD-MMM-YYYY"
                      value={dateOfBirthDisplay || (dateOfBirth ? formatDateToDisplay(dateOfBirth) : "")}
                      onChange={handleDateChange}
                      onBlur={(e) => {
                        // Ensure date is in correct format on blur
                        if (e.target.value && !dateOfBirth) {
                          const parsed = parseDateFromDisplay(e.target.value);
                          if (parsed) {
                            setDateOfBirth(parsed);
                          }
                        }
                      }}
                    />
                  </div>

                  <div className="form-group-container">
                    <label className="form-label">Gender <span className="text-red-500">*</span></label>
                    <select
                      className="form-select"
                      value={gender}
                      onChange={(e) => setGender(e.target.value)}
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </>
              )}
            </div>
          </div>

          {isPanValid && (
            <>
              <div className="form-group">
                <div className="form-group-container">
                  <label className="form-label">Enter Email Id</label>
                  <input
                    type="email"
                    className="form-input"
                    placeholder="example@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>

                <div className="form-group-container">
                  <label className="form-label">Enter Aadhar Number <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="XXXX XXXX XXXX"
                    value={aadharNumber}
                    onChange={handleAadharChange}
                    maxLength={12}
                  />
                </div>
              </div>

              {/* Consent Section */}
              <div className="consent-section">
                <div className="checkbox-container-main">
                  <input
                    type="checkbox"
                    className="checkbox-input-main"
                    checked={consent1}
                    onChange={handleconsentChange}
                  />
                  <div className="consent-text-container">
                    <div className="consent-item">
                      <span className="bullet-point">•</span>
                      <span className="consent-text">
                        I authorize AyushPay and its lending partners to access
                        my credit report for processing my medical loan
                        application.
                      </span>
                    </div>
                    <div className="consent-item">
                      <span className="bullet-point">•</span>
                      <span className="consent-text">
                        I confirm that my annual income exceeds ₹2,50,000 and is
                        directly credited to my bank account.
                      </span>
                    </div>
                    <div className="consent-item">
                      <span className="bullet-point">•</span>
                      <span className="consent-text">
                        I confirm that I am applying this loan for a medical
                        purpose only.
                      </span>
                    </div>

                    {/* WhatsApp consent inside the same container */}
                    <div className="checkbox-container-secondary">
                      <input
                        type="checkbox"
                        className="checkbox-input-main"
                        checked={whatsappConsent}
                        onChange={(e) => setWhatsappConsent(e.target.checked)}
                      />
                      <div className="consent-text-container">
                        <div className="consent-item">
                          <span className="bullet-point">•</span>
                          <span className="consent-text">
                            I agree to receive updates on WhatsApp.
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="fixed-button-container">
        <button
          className="continue-button"
          onClick={handleSubmit}
          disabled={!isFormValid}
        >
          CONTINUE
        </button>
      </div>

    </div>
  );
};

export default PersonalDetails;
